<template>
  <div :class="['image-cropper', containerClass]">
    <div class="image-container">
      <div
        class="image-viewport"
        @mousedown="grabImage">
        <img
          class="image"
          :style="imageStyle"
          :src="url">
      </div>
    </div>
    <div class="slider-container">
      <input
        class="slider"
        type="range"
        step="0.01"
        :min="minZoom"
        :max="maxZoom"
        :value="zoom"
        @input="setZoom($event.target.value)">
    </div>
  </div>
</template>

<script>
  export default {
    name: 'image-cropper',

    props: {
      containerClass: {
        type: String,
        default: null
      },
      boundary: {
        type: Object,
        default: () => ({ width: '100%', height: '100%' })
      },
      viewport: {
        type: Object,
        default: () => ({ width: '50%', height: '50%' })
      },
      url: {
        type: String,
        default: null
      },
      crop: {
        type: Object, // Structure returned by getPoints()
        default: null
      }
    },

    data() {
      return {
        viewX: 0.5, // [0-1]
        viewY: 0.5, // [0-1]
        zoom: 1, // 2 == image is 2 times larger than viewport
        minZoom: 1,
        maxZoom: 15,
        css: {
          imageWidth: null,
          imageHeight: null,
          imageLeft: 0,
          imageTop: 0
        },
        grabInfo: null
      };
    },

    mounted() {
      this.updateBoundaryCss();
      this.updateViewportCss();
      this.imageEl.addEventListener('load', () => {
        if (this.crop) {
          this.setPoints(this.crop);
        } else {
          this.resetPoints();
        }
      });
      this.boundaryEl.addEventListener('wheel', ev => {
        if (ev.deltaY !== 0) {
          ev.preventDefault();
          // Our zoom scale isn't linear, and that becomes noticable the more we
          // zoom in. Using a normalization factor will help with that.
          const normFactor = this.zoom;
          this.setZoom(this.zoom - ev.deltaY * 0.001 * normFactor);
        }
      });
    },

    computed: {
      boundaryEl: function() {
        return this.$el.querySelector('.image-container');
      },
      viewportEl() {
        return this.$el.querySelector('.image-viewport');
      },
      imageEl() {
        return this.$el.querySelector('.image');
      },
      sliderEl() {
        return this.$el.querySelector('.slider');
      },
      imageStyle() {
        return {
          width:  this.css.imageWidth || 'auto',
          height: this.css.imageHeight || 'auto',
          left:   this.css.imageLeft,
          top:    this.css.imageTop
        };
      },
    },

    methods: {
      getPoints() {
        const imgEl = this.imageEl;
        const imgW = imgEl.naturalWidth;
        const imgH = imgEl.naturalHeight;

        const viewW = this.getViewW();
        const viewH = this.getViewH();
        const viewX1 = this.viewX - viewW / 2;
        const viewY1 = this.viewY - viewH / 2;
        const viewX2 = this.viewX + viewW / 2;
        const viewY2 = this.viewY + viewH / 2;

        return {
          x1: Math.round(viewX1 * imgW),
          y1: Math.round(viewY1 * imgH),
          x2: Math.round(viewX2 * imgW),
          y2: Math.round(viewY2 * imgH),
          width: Math.round(viewW * imgW),
          height: Math.round(viewH * imgH)
        };
      },

      setPoints(points) {
        const imgEl = this.imageEl;
        const imgW = imgEl.naturalWidth;
        const imgH = imgEl.naturalHeight;

        const pointsW = points.width || (points.x2 - points.x1);
        const pointsH = points.height || (points.y2 - points.y1);

        const viewX1 = points.x1 / imgW;
        const viewX2 = (points.x1 + pointsW) / imgW;
        const viewY1 = points.y1 / imgH;
        const viewY2 = (points.y1 + pointsH) / imgH;
        const viewW = viewX2 - viewX1;
        const viewH = viewY2 - viewY1;
        const imgToView = this.getImageToViewRatio();

        this.viewX = (viewX1 + viewX2) / 2;
        this.viewY = (viewY1 + viewY2) / 2;
        this.setZoom(1 / (imgToView.width * viewW));
      },

      resetPoints() {
        this.viewX = 0.5;
        this.viewY = 0.5;
        this.setZoom(1);
      },

      getImageToViewRatio() {
        const viewEl = this.viewportEl;
        const viewW = viewEl.clientWidth;
        const viewH = viewEl.clientHeight;

        const imgEl = this.imageEl;
        const imgW = imgEl.clientWidth;
        const imgH = imgEl.clientHeight;

        const ratioX = imgW / viewW;
        const ratioY = imgH / viewH;

        if (ratioX <= ratioY) {
          return {
            width: 1,
            height: ratioY / ratioX
          };
        } else {
          return {
            width: ratioX / ratioY,
            height: 1
          };
        }
      },

      // Size of viewport compared to image size
      // Range: [0-1]
      getViewW() {
        const imgToView = this.getImageToViewRatio();
        return 1 / (this.zoom * imgToView.width);
      },

      // Size of viewport compared to image size
      // Range: [0-1]
      getViewH() {
        const imgToView = this.getImageToViewRatio();
        return 1 / (this.zoom * imgToView.height);
      },

      boundImage() {
        // Bound size
        if (this.zoom < 1) {
          this.zoom = 1;
        }

        // Bound position
        const viewW = this.getViewW();
        const viewH = this.getViewH();
        if (this.viewX - viewW / 2 < 0) {
          this.viewX = viewW / 2;
        }
        if (this.viewX + viewW / 2 > 1) {
          this.viewX = 1 - viewW / 2;
        }
        if (this.viewY - viewH / 2 < 0) {
          this.viewY = viewH / 2;
        }
        if (this.viewY + viewH / 2 > 1) {
          this.viewY = 1 - viewH / 2;
        }
      },

      updateImageCss() {
        const imgToView = this.getImageToViewRatio();
        const viewW = this.getViewW(); // These are computed, so cache for performance
        const viewH = this.getViewH();

        if (imgToView.width === 1) {
          this.css.imageWidth = `${1 / viewW * 100}%`;
          this.css.imageHeight = null;
        } else {
          this.css.imageWidth = null;
          this.css.imageHeight = `${1 / viewH * 100}%`;
        }

        const viewX1 = this.viewX - viewW / 2; // The viewX value at the left of the viewport
        const viewY1 = this.viewY - viewH / 2;
        const viewXSlop = 1 - viewW; // The amount "play" or movement that is allowed for the viewport
        const viewYSlop = 1 - viewH;
        const imageLeftSlop = 1 / viewW - 1; // The max percentage that the image should move
        const imageTopSlop = 1 / viewH - 1;
        if (viewXSlop === 0) {
          this.css.imageLeft = '0';
        } else {
          this.css.imageLeft = `${-1 * viewX1 / viewXSlop * imageLeftSlop * 100}%`;
        }
        if (viewYSlop === 0) {
          this.css.imageTop = '0';
        } else {
          this.css.imageTop = `${-1 * viewY1 / viewYSlop * imageTopSlop * 100}%`;
        }
      },

      updateBoundaryCss() {
        const boundEl = this.boundaryEl;
        boundEl.style.width = this.boundary.width;
        boundEl.style.height = this.boundary.height;
      },

      updateViewportCss() {
        const viewEl = this.viewportEl;
        viewEl.style.width = this.viewport.width;
        viewEl.style.height = this.viewport.height;
        viewEl.style.left = `calc((100% - ${this.viewport.width}) / 2)`;
        viewEl.style.top = `calc((100% - ${this.viewport.height}) / 2)`;
      },

      setZoom(zoom) {
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, parseFloat(zoom)));
        this.boundImage();
        this.updateImageCss();
      },

      grabImage(ev) {
        if (ev.button !== 0) {
          return;
        }
        ev.preventDefault();
        this.grabInfo = {
          mouseX: ev.pageX || (ev.touches ? ev.touches[0].pageX : null),
          mouseY: ev.pageY || (ev.touches ? ev.touches[0].pageY : null),
          viewX: this.viewX,
          viewY: this.viewY
        };
        window.addEventListener('mousemove', this.moveImage);
        window.addEventListener('touchmove', this.moveImage);
        window.addEventListener('mouseup', this.releaseImage);
        window.addEventListener('touchend', this.releaseImage);
      },

      moveImage(ev) {
        ev.preventDefault();
        const currentMouseX = ev.pageX || (ev.touches ? ev.touches[0].pageX : null);
        const currentMouseY = ev.pageY || (ev.touches ? ev.touches[0].pageY : null);
        const diffMouseX = currentMouseX - this.grabInfo.mouseX;
        const diffMouseY = currentMouseY - this.grabInfo.mouseY;

        const viewEl = this.viewportEl;
        const diffViewX = diffMouseX / viewEl.clientWidth * this.getViewW();
        const diffViewY = diffMouseY / viewEl.clientHeight * this.getViewH();

        this.viewX = this.grabInfo.viewX - diffViewX;
        this.viewY = this.grabInfo.viewY - diffViewY;
        this.boundImage();
        this.updateImageCss();
      },

      releaseImage(ev) {
        if (!this.grabInfo) {
          return;
        }
        this.grabInfo = null;
        window.removeEventListener('mousemove', this.moveImage);
        window.removeEventListener('touchmove', this.moveImage);
        window.removeEventListener('mouseup', this.releaseImage);
        window.removeEventListener('touchend', this.releaseImage);
      }
    },

    watch: {
      boundary: 'updateBoundaryCss',
      viewport: 'updateViewportCss',
      crop: function(crop) {
        if (crop) {
          this.setPoints(crop);
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .image-cropper {
    .image-container {
      background-color: white;
      position: relative;
      overflow: hidden;
      z-index: 0;
    }
    .image-viewport {
      border: 2px solid white;
      box-shadow: 0 0 2000px 2000px rgba(0, 0, 0, 0.5);
      position: absolute;
      .image {
        position: absolute;
        max-width: none;
        max-height: none;
        z-index: -1;
      }
    }
    .slider-container {
      margin: 20px 20px 0 20px;
    }
  }
</style>

<style lang="scss">
  .image-cropper {
    .slider-container {
      // Prepare for fun! Sliders are apparently hard to customize:
      // http://brennaobrien.com/blog/2014/05/style-input-type-range-in-every-browser.html
      input[type=range] {
        -webkit-appearance: none;
        width: 100%;
        background: none;
      }

      @mixin track() {
        width: 100%;
        height: 5px;
        background: #ddd;
        border: none;
        border-radius: 8px;
      }
      input[type=range]::-webkit-slider-runnable-track {
        @include track();
      }
      input[type=range]::-moz-range-track {
        @include track();
      }
      input[type=range]::-ms-track {
        width: 100%;
        height: 5px;
        background: transparent;
        border-color: transparent;
        border-width: 6px 0;
        color: transparent;
      }
      input[type=range]::-ms-fill-lower {
        background: #777;
        border-radius: 10px;
      }
      input[type=range]::-ms-fill-upper {
        background: #ddd;
        border-radius: 10px;
      }

      @mixin thumb() {
        border: 1px solid #444;
        height: 16px;
        width: 16px;
        border-radius: 50%;
        background-color: #ddd;
      }
      input[type=range]::-webkit-slider-thumb {
        @include thumb();
        -webkit-appearance: none;
        margin-top: -5px;
      }
      input[type=range]::-moz-range-thumb {
        @include thumb();
      }
      input[type=range]::-ms-thumb {
        @include thumb();
      }

      input[type=range]:focus {
        outline: none;
      }

      input[type=range]:focus::-webkit-slider-runnable-track {
        background: #ccc;
      }
      input[type=range]:focus::-moz-range-track {
        background: #ccc;
      }
      input[type=range]:focus::-ms-fill-lower {
        background: #888;
      }
      input[type=range]:focus::-ms-fill-upper {
        background: #ccc;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// index.vue?132ff55c